import { useForm } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { PolicySetting } from '@kirin/api-client/contracts/policies';
import { PolicyKey } from '@kirin/api-client/contracts/policies';
import { PolicySettingField } from './PolicySettingField';
import { BuiltInCatalogLink } from './BuiltInCatalogLink';
import { shouldHidePolicySetting } from './policySettingsVisibility';
import { PolicySettingsActions } from './PolicySettingsActions';
import { Separator } from '@/components/ui/separator';
import { Info } from 'lucide-react';
import {
  policySettingsToFormData,
  type PolicySettingsFormData,
  type UsePolicySettingsHook,
} from '../types';
import { Button } from '@/components/ui/button';
import { YaraExclusionsControl } from '../../YaraExclusionsControl';
import {
  useYaraExclusions,
  useAddYaraExclusion,
  useRemoveYaraExclusion,
} from '@/hooks/api/useYaraExclusions';
import { useRoleCheck, useIsSupport } from '@/hooks/useRoleCheck';
import { OrganizationRole } from '@/lib/roles';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { useUnsavedChangeActions } from '@/hooks/useScopeChangeHooks';
import {
  AllowBlockPolicySettingsForm,
  isAllowBlockPolicyKey,
} from '../allow-block';

/**
 * `extensions-management` and `mcp-server-sources` use Allow/Block UI from
 * `allow-block/components/AllowBlockPolicySettingsForm` (Blocked list tri-state inside expanded
 * settings; row-level mode is hidden via `policyKeyHidesOuterModeControls`).
 */

interface SettingsGroup {
  name: string | null;
  settings: PolicySetting[];
}

function groupSettings(settings: PolicySetting[]): SettingsGroup[] {
  const groups = new Map<string | null, PolicySetting[]>();

  for (const setting of settings) {
    const groupName = setting.group ?? null;
    if (!groups.has(groupName)) {
      groups.set(groupName, []);
    }
    const groupSettings = groups.get(groupName);
    if (groupSettings) {
      groupSettings.push(setting);
    }
  }

  const result: SettingsGroup[] = [];
  if (groups.has(null)) {
    const nullGroupSettings = groups.get(null);
    if (nullGroupSettings) {
      result.push({ name: null, settings: nullGroupSettings });
      groups.delete(null);
    }
  }
  for (const [name, groupSettings] of groups) {
    result.push({ name, settings: groupSettings });
  }

  return result;
}

interface PolicySettingsFormProps {
  policyKey: string;
  usePolicySettings: UsePolicySettingsHook;
  saving: boolean;
  onSave: (policyKey: string, settings: Record<string, unknown>) => void;
  onReset: (policyKey: string) => void;
  readOnly?: boolean;
  onPolicySettingsDirty?: (dirty: boolean) => void;
  scope?: string;
}

export function PolicySettingsForm({
  policyKey,
  usePolicySettings,
  saving,
  onSave,
  onReset,
  readOnly = false,
  onPolicySettingsDirty,
  scope,
}: PolicySettingsFormProps) {
  const { markDirty, markClean } = useUnsavedChangeActions();
  // Form fetches its own data - eliminates prop-to-state sync anti-pattern
  const queryResult = usePolicySettings(policyKey);
  const {
    data: settingsData,
    isLoading,
    isError,
    error,
    refetch,
  } = queryResult;

  // Check feature flags
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.EXTENSION_CODE_SCAN,
    flagsmithFeatureFlags.HOOK_PREVENTION_MODE,
    flagsmithFeatureFlags.AI_MCP_TOOL_ANALYSIS,
    flagsmithFeatureFlags.MCP_TOOL_RESPONSE_SCANNING,
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
  ]);
  const isExtensionCodeScanEnabled =
    flags[flagsmithFeatureFlags.EXTENSION_CODE_SCAN] ?? false;
  const isHookPreventionModeEnabled =
    flags[flagsmithFeatureFlags.HOOK_PREVENTION_MODE] ?? false;
  const isAiMcpToolAnalysisEnabled =
    flags[flagsmithFeatureFlags.AI_MCP_TOOL_ANALYSIS] ?? false;
  const isMcpToolResponseScanningEnabled =
    flags[flagsmithFeatureFlags.MCP_TOOL_RESPONSE_SCANNING] ?? false;
  const isCapabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;

  const form = useForm<PolicySettingsFormData>({
    defaultValues: {},
    mode: 'onChange',
  });

  const { handleSubmit, reset, formState, watch } = form;

  // Watch extensionsCodeScan value to conditionally show YARA exclusions
  const extensionsCodeScan = watch('extensionsCodeScan');
  // Watch autoRemoveVulnerableExtensions to grey out Removal Severity Threshold when policy is off
  const autoRemoveVulnerableExtensions = watch(
    'autoRemoveVulnerableExtensions'
  );

  // Get organization ID for YARA exclusions (if in organization context)
  const { organizationId } = useParams<{ organizationId?: string }>();
  // For personal accounts, user can always modify their own exclusions
  // For organizations, check role permissions
  const { hasAccess: canModifyYaraExclusions } = useRoleCheck(
    organizationId
      ? {
          param: 'organizationId',
          anyOf: [OrganizationRole.Owner, OrganizationRole.Manager],
        }
      : undefined,
    organizationId
  );
  // Personal accounts can always modify their own exclusions
  const canModify = organizationId ? canModifyYaraExclusions : true;

  // useIsSupport(undefined) resolves hasAccess:true (no rolesConfig => "any
  // authenticated user"), so we only trust it for organization scope.
  const { isSupport } = useIsSupport(organizationId);
  const isSupportReadOnly = !!organizationId && isSupport;

  // YARA exclusions hooks (if extensionsCodeScan is enabled, works for both personal and organization)
  const shouldLoadYaraExclusions = extensionsCodeScan === true;
  const yaraExclusionsQuery = useYaraExclusions(
    organizationId,
    shouldLoadYaraExclusions
  );
  const addYaraExclusionMutation = useAddYaraExclusion(organizationId);
  const removeYaraExclusionMutation = useRemoveYaraExclusion(organizationId);

  // Determine which settings should be hidden based on feature flags and policy mode
  // Prevention toggles (auto-remove, etc.) are always hidden; only Off/Monitor/Enforce selector is used.
  const shouldHideSetting = useMemo(() => {
    return (settingKey: string): boolean =>
      shouldHidePolicySetting(settingKey, policyKey, {
        isExtensionCodeScanEnabled,
        isHookPreventionModeEnabled,
        isAiMcpToolAnalysisEnabled,
        isMcpToolResponseScanningEnabled,
        isCapabilityDetectionEnabled,
      });
  }, [
    isExtensionCodeScanEnabled,
    isHookPreventionModeEnabled,
    isAiMcpToolAnalysisEnabled,
    isMcpToolResponseScanningEnabled,
    isCapabilityDetectionEnabled,
    policyKey,
  ]);

  // All settings (including hidden ones) to ensure form registration
  const allSettings = settingsData?.settings ?? [];

  // Reset form when data loads
  useEffect(() => {
    if (settingsData) {
      const formData = policySettingsToFormData(settingsData);
      reset(formData);
    }
  }, [settingsData, reset]);

  useEffect(() => {
    if (isAllowBlockPolicyKey(policyKey)) {
      return;
    }
    onPolicySettingsDirty?.(formState.isDirty);
    if (!scope) return;
    if (formState.isDirty) {
      markDirty(scope);
    } else {
      markClean(scope);
    }

    return () => {
      onPolicySettingsDirty?.(false);
      markClean(scope);
    };
  }, [
    policyKey,
    formState.isDirty,
    onPolicySettingsDirty,
    scope,
    markDirty,
    markClean,
  ]);

  const onSubmit = async (data: PolicySettingsFormData) => {
    if (readOnly) return;
    try {
      await onSave(policyKey, data);
      reset(data, { keepValues: true });
    } catch {
      // Keep form dirty so user can retry
    }
  };

  const handleCancel = () => {
    if (readOnly || !settingsData) return;
    const formData = policySettingsToFormData(settingsData);
    reset(formData);
  };

  const handleReset = async () => {
    if (readOnly) return;
    await onReset(policyKey);
    // Reset will happen automatically via useEffect when settingsData updates
  };

  if (isLoading) {
    return (
      <div className="pt-4">
        <div className="text-sm text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  if (policyKey === PolicyKey.AiInstructions && isError) {
    return (
      <div className="pt-4 space-y-2">
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Could not load settings.'}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!settingsData) {
    return (
      <div className="pt-4">
        <div className="text-sm text-muted-foreground">
          No settings available.
        </div>
      </div>
    );
  }

  if (isAllowBlockPolicyKey(policyKey)) {
    return (
      <AllowBlockPolicySettingsForm
        policyKey={policyKey}
        usePolicySettings={usePolicySettings}
        saving={saving}
        onSave={onSave}
        onReset={onReset}
        readOnly={readOnly}
        isSupportReadOnly={isSupportReadOnly}
        onPolicySettingsDirty={onPolicySettingsDirty}
        scope={scope}
      />
    );
  }

  const hasCustomizedSettings = allSettings.some(s => s.isCustomized);

  // Separate settings: patterns first, sensitivity, then AI settings
  // Only for cursor-database policy
  const isCursorDatabasePolicy = policyKey === 'cursor-database';
  const aiSettingKeys = ['enableCursorAIAnalysis'];
  const patternSettings = isCursorDatabasePolicy
    ? allSettings.filter(
        s =>
          !aiSettingKeys.includes(s.key) &&
          s.key !== 'aiCodeSensitivity' &&
          s.key !== 'credentialPatterns' // Not shown in UI (merged with maliciousPatterns for display)
      )
    : [];
  const sensitivitySettings = isCursorDatabasePolicy
    ? allSettings.filter(s => s.key === 'aiCodeSensitivity')
    : [];
  const aiSettings = isCursorDatabasePolicy
    ? allSettings.filter(s => aiSettingKeys.includes(s.key))
    : [];

  // Separate settings for mcp-security policy: hidden characters, dependencies and tool descriptions
  const isMcpSecurityPolicy = policyKey === 'mcp-security';
  const enableHiddenCharactersScan = isMcpSecurityPolicy
    ? allSettings.find(s => s.key === 'enableHiddenCharactersScan')
    : undefined;
  const enableDependenciesScan = isMcpSecurityPolicy
    ? allSettings.find(s => s.key === 'enableDependenciesScan')
    : undefined;
  const enableToolDescriptionsScan = isMcpSecurityPolicy
    ? allSettings.find(s => s.key === 'enableToolDescriptionsScan')
    : undefined;
  // KIR-3157: MCP tool-response scan toggle. Visibility is gated by the
  // mcp_tool_response_scanning flag via shouldHideSetting (the setting is
  // always present in the backend list; the flag controls whether it renders).
  const enableToolResponseScan = isMcpSecurityPolicy
    ? allSettings.find(s => s.key === 'enableToolResponseScan')
    : undefined;
  const dependenciesSettings = isMcpSecurityPolicy
    ? allSettings.filter(s => s.group === 'Dependencies')
    : [];
  const toolDescriptionsSettings = isMcpSecurityPolicy
    ? allSettings.filter(s => s.group === 'Tool Descriptions')
    : [];

  // Separate settings for extension-security policy to control ordering
  const isExtensionSecurityPolicy = policyKey === 'extension-security';
  const extensionSecuritySettingsBeforeCodeScan = isExtensionSecurityPolicy
    ? allSettings.filter(
        s =>
          s.key !== 'extensionsCodeScan' &&
          s.key !== 'extensionsPackagesOsvDetection' &&
          s.key !== 'customCapabilityRules'
      )
    : [];
  const extensionsCodeScanSetting = isExtensionSecurityPolicy
    ? allSettings.find(s => s.key === 'extensionsCodeScan')
    : undefined;
  const extensionsPackagesOsvDetectionSetting = isExtensionSecurityPolicy
    ? allSettings.find(s => s.key === 'extensionsPackagesOsvDetection')
    : undefined;
  const customCapabilityRulesSetting = isExtensionSecurityPolicy
    ? allSettings.find(s => s.key === 'customCapabilityRules')
    : undefined;

  // Check if we should show YARA exclusions (for extension-security policy when enabled, works for both personal and organization)
  const showYaraExclusions =
    policyKey === 'extension-security' && extensionsCodeScan === true;

  const settingsGroups = groupSettings(allSettings);
  const hasMultipleGroups =
    settingsGroups.length > 1 ||
    (settingsGroups.length === 1 && settingsGroups[0].name !== null);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="pt-4 space-y-4">
      {/* For cursor-database: Show settings with separators */}
      {isCursorDatabasePolicy && (
        <>
          {patternSettings.length > 0 && <Separator label="Detected Rules" />}

          {patternSettings.length > 0 && (
            <div className="space-y-4">
              {patternSettings.map(setting => (
                <PolicySettingField
                  key={setting.key}
                  setting={setting}
                  policyKey={policyKey}
                  control={form.control}
                  readOnly={readOnly}
                  isSupportReadOnly={isSupportReadOnly}
                  hidden={shouldHideSetting(setting.key)}
                  isDirty={formState.isDirty}
                />
              ))}
            </div>
          )}

          {sensitivitySettings.length > 0 && patternSettings.length > 0 && (
            <Separator label="Rules Sensitivity Settings" />
          )}

          {sensitivitySettings.length > 0 && (
            <div className="space-y-4">
              {sensitivitySettings.map(setting => (
                <PolicySettingField
                  key={setting.key}
                  setting={setting}
                  policyKey={policyKey}
                  control={form.control}
                  readOnly={readOnly}
                  isSupportReadOnly={isSupportReadOnly}
                  hidden={shouldHideSetting(setting.key)}
                  isDirty={formState.isDirty}
                />
              ))}
            </div>
          )}

          {aiSettings.length > 0 &&
            (patternSettings.length > 0 || sensitivitySettings.length > 0) && (
              <Separator label="Kirin AI Service Settings" />
            )}

          {aiSettings.length > 0 && (
            <div className="space-y-4">
              {aiSettings.map(setting => (
                <PolicySettingField
                  key={setting.key}
                  setting={setting}
                  policyKey={policyKey}
                  control={form.control}
                  readOnly={readOnly}
                  isSupportReadOnly={isSupportReadOnly}
                  hidden={shouldHideSetting(setting.key)}
                  isDirty={formState.isDirty}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* For mcp-security: Show settings with custom structure */}
      {isMcpSecurityPolicy && (
        <>
          {/* Enable Hidden Characters Scan toggle */}
          {enableHiddenCharactersScan && (
            <PolicySettingField
              setting={enableHiddenCharactersScan}
              policyKey={policyKey}
              control={form.control}
              readOnly={readOnly}
              isSupportReadOnly={isSupportReadOnly}
              hidden={shouldHideSetting(enableHiddenCharactersScan.key)}
              isDirty={formState.isDirty}
            />
          )}

          {/* MCP Tool Responses scan toggle (KIR-3157; flag-gated via shouldHideSetting) */}
          {enableToolResponseScan && (
            <PolicySettingField
              setting={enableToolResponseScan}
              policyKey={policyKey}
              control={form.control}
              readOnly={readOnly}
              isSupportReadOnly={isSupportReadOnly}
              hidden={shouldHideSetting(enableToolResponseScan.key)}
              isDirty={formState.isDirty}
            />
          )}

          {/* MCP Server Dependencies section */}
          <Separator label="MCP Server Dependencies" />

          {/* Enable Dependencies Scan toggle */}
          {enableDependenciesScan && (
            <PolicySettingField
              setting={enableDependenciesScan}
              policyKey={policyKey}
              control={form.control}
              readOnly={readOnly}
              isSupportReadOnly={isSupportReadOnly}
              hidden={shouldHideSetting(enableDependenciesScan.key)}
              isDirty={formState.isDirty}
            />
          )}

          {/* Dependencies settings */}
          {dependenciesSettings.length > 0 && (
            <div className="space-y-4">
              {dependenciesSettings.map(setting => (
                <PolicySettingField
                  key={setting.key}
                  setting={setting}
                  policyKey={policyKey}
                  control={form.control}
                  readOnly={readOnly}
                  isSupportReadOnly={isSupportReadOnly}
                  hidden={shouldHideSetting(setting.key)}
                  isDirty={formState.isDirty}
                />
              ))}
            </div>
          )}

          {/* MCP Server Tool Descriptions section */}
          <Separator label="MCP Server Tool Descriptions" />

          {/* Enable Tool Descriptions Scan toggle */}
          {enableToolDescriptionsScan && (
            <PolicySettingField
              setting={enableToolDescriptionsScan}
              policyKey={policyKey}
              control={form.control}
              readOnly={readOnly}
              isSupportReadOnly={isSupportReadOnly}
              hidden={shouldHideSetting(enableToolDescriptionsScan.key)}
              isDirty={formState.isDirty}
            />
          )}

          {/* Tool Descriptions settings */}
          {toolDescriptionsSettings.length > 0 && (
            <div className="space-y-4">
              {toolDescriptionsSettings.map(setting => (
                <PolicySettingField
                  key={setting.key}
                  setting={setting}
                  policyKey={policyKey}
                  control={form.control}
                  readOnly={readOnly}
                  isSupportReadOnly={isSupportReadOnly}
                  hidden={shouldHideSetting(setting.key)}
                  isDirty={formState.isDirty}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* For extension-security: Show settings with custom ordering */}
      {isExtensionSecurityPolicy && (
        <div className="space-y-4">
          {/* Settings before Extensions Code Scan (e.g., dangerousPermissions, untrustedPublishers, etc.) */}
          {extensionSecuritySettingsBeforeCodeScan.map(setting => (
            <PolicySettingField
              key={setting.key}
              setting={setting}
              policyKey={policyKey}
              control={form.control}
              readOnly={
                readOnly ||
                (setting.key === 'removalSeverityThreshold' &&
                  !autoRemoveVulnerableExtensions)
              }
              isSupportReadOnly={isSupportReadOnly}
              hidden={shouldHideSetting(setting.key)}
              isDirty={formState.isDirty}
            />
          ))}

          {/* Extensions Code Scan toggle */}
          {extensionsCodeScanSetting && (
            <PolicySettingField
              setting={extensionsCodeScanSetting}
              policyKey={policyKey}
              control={form.control}
              readOnly={readOnly}
              isSupportReadOnly={isSupportReadOnly}
              hidden={shouldHideSetting(extensionsCodeScanSetting.key)}
              isDirty={formState.isDirty}
            />
          )}

          {/* YARA Exclusions Control - shown right after Extensions Code Scan when enabled */}
          {showYaraExclusions && (
            <div className="ml-6">
              <div className="pt-4 pb-2">
                <YaraExclusionsControl
                  exclusions={yaraExclusionsQuery.data ?? []}
                  isLoading={yaraExclusionsQuery.isLoading}
                  onAdd={async data =>
                    addYaraExclusionMutation.mutateAsync(data)
                  }
                  onRemove={async extensionId => {
                    await removeYaraExclusionMutation.mutateAsync(extensionId);
                  }}
                  canModify={canModify && !readOnly}
                />
              </div>
            </div>
          )}

          {/* Dependencies Scan toggle - appears last */}
          {extensionsPackagesOsvDetectionSetting && (
            <PolicySettingField
              setting={extensionsPackagesOsvDetectionSetting}
              policyKey={policyKey}
              control={form.control}
              readOnly={readOnly}
              isSupportReadOnly={isSupportReadOnly}
              hidden={shouldHideSetting(
                extensionsPackagesOsvDetectionSetting.key
              )}
              isDirty={formState.isDirty}
            />
          )}

          {/* Custom Capability Rules — customizable, opens a dedicated page */}
          {customCapabilityRulesSetting && (
            <PolicySettingField
              setting={customCapabilityRulesSetting}
              policyKey={policyKey}
              control={form.control}
              readOnly={readOnly}
              isSupportReadOnly={isSupportReadOnly}
              hidden={shouldHideSetting(customCapabilityRulesSetting.key)}
              isDirty={formState.isDirty}
            />
          )}

          {/* Built-in Detection Catalog — read-only, opens a full page */}
          {isCapabilityDetectionEnabled && <BuiltInCatalogLink />}
        </div>
      )}

      {policyKey === PolicyKey.AiInstructions && (
        <div className="mb-4 flex gap-2 text-xs font-normal leading-relaxed text-muted-foreground">
          <Info
            className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-70"
            aria-hidden
          />
          <p className="m-0">
            Please note: Some AI Instructions may not be checked due to access
            or synchronization limitations (for example, files that aren&apos;t
            available locally or haven&apos;t been synced yet).
          </p>
        </div>
      )}

      {/* For other policies: Use group-based rendering */}
      {!isCursorDatabasePolicy &&
        !isMcpSecurityPolicy &&
        !isExtensionSecurityPolicy && (
          <div className="space-y-4">
            {settingsGroups.map((group, groupIndex) => (
              <div key={group.name ?? 'ungrouped'}>
                {hasMultipleGroups && group.name && (
                  <div className="flex items-center gap-2 mb-3 mt-2">
                    <span className="text-sm font-medium text-muted-foreground">
                      {group.name}
                    </span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                )}
                <div className="space-y-4">
                  {group.settings.map(setting => (
                    <PolicySettingField
                      key={setting.key}
                      setting={setting}
                      policyKey={policyKey}
                      control={form.control}
                      readOnly={readOnly}
                      isSupportReadOnly={isSupportReadOnly}
                      hidden={shouldHideSetting(setting.key)}
                      isDirty={formState.isDirty}
                    />
                  ))}
                </div>
                {hasMultipleGroups &&
                  groupIndex < settingsGroups.length - 1 && (
                    <div className="h-2" />
                  )}
              </div>
            ))}
          </div>
        )}

      {/* Main save/cancel/reset buttons */}
      <PolicySettingsActions
        hasCustomizedSettings={hasCustomizedSettings}
        policyName={settingsData.policy.name}
        isDirty={formState.isDirty}
        isSubmitting={saving}
        readOnly={readOnly}
        onSave={handleSubmit(onSubmit)}
        onReset={handleReset}
        onCancel={handleCancel}
      />
    </form>
  );
}
